// <newsletter-popup>: opens the newsletter modal once per session, and stops asking once
// someone subscribes or says no.
//
// Three layers of suppression, in order of authority:
//   1. Server-side — sections/newsletter-popup.liquid renders nothing at all when a
//      signed-in customer already has accepts_marketing. Nothing here can run.
//   2. localStorage — "subscribed" is permanent; "dismissed" expires after remind-days.
//      This is what covers anonymous visitors, whose subscription status Liquid cannot see.
//   3. sessionStorage — at most one appearance per browsing session, even on a long visit
//      across many pages.
customElements.define("newsletter-popup", class extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;

    this.KEY = "jg-newsletter";
    this.SESSION_KEY = "jg-newsletter-seen";
    this.remindMs = (Number(this.dataset.remindDays) || 14) * 86400000;

    // Every subscribe finishes as a NAVIGATION — Shopify's forms post and redirect, and
    // no in-page path exists (see assets/form-post.js). So the confirmation always arrives
    // as a flag on the URL of this page load rather than as an event on a live element:
    // stop asking, and say so, since the modal that asked went with the old document.
    this.absorbPostedFlag();

    if (this.suppressed()) return;

    // The theme editor should always show the popup, or it cannot be previewed once it has
    // been dismissed once.
    const design = window.Shopify && window.Shopify.designMode;
    if (!design && this.seenThisSession()) return;

    customElements.whenDefined("theme-modal").then(() => this.schedule(design));
  }

  // ?customer_posted=true is Shopify's own success flag, and the only thing on this store
  // that posts form_type=customer is a newsletter subscribe (both here and in the on-page
  // newsletter section), so it is safe to read as "subscribed".
  absorbPostedFlag() {
    let posted = false;
    try {
      posted = new URL(window.location.href).searchParams.get("customer_posted") === "true";
    } catch (e) { return; }
    if (!posted) return;

    this.remember("subscribed");
    // assets/form-post.js re-opens this modal when the subscribe came from it, and the
    // re-opened modal already shows the confirmation. Toasting on top of that tells the
    // visitor the same thing twice, in two places.
    if (this.querySelector("theme-modal[data-form-restored]")) return;
    // One toast per landing, not one per refresh: the flag stays in the URL across reloads.
    const seen = "jg-newsletter-toasted";
    try {
      if (sessionStorage.getItem(seen) === window.location.search) return;
      sessionStorage.setItem(seen, window.location.search);
    } catch (e) {}
    const msg = this.dataset.successText;
    if (msg && window.theme && window.theme.toast) window.theme.toast(msg, { type: "success" });
  }

  read() {
    try { return JSON.parse(localStorage.getItem(this.KEY)) || null; } catch (e) { return null; }
  }

  remember(state) {
    try { localStorage.setItem(this.KEY, JSON.stringify({ state: state, at: Date.now() })); } catch (e) {}
  }

  suppressed() {
    const rec = this.read();
    if (!rec) return false;
    if (rec.state === "subscribed") return true;
    return rec.state === "dismissed" && Date.now() - (rec.at || 0) < this.remindMs;
  }

  seenThisSession() {
    try { return sessionStorage.getItem(this.SESSION_KEY) === "1"; } catch (e) { return false; }
  }

  markSeen() {
    try { sessionStorage.setItem(this.SESSION_KEY, "1"); } catch (e) {}
  }

  schedule(design) {
    const modal = this.querySelector("theme-modal");
    if (!modal || typeof modal.open !== "function") return;

    const delay = design ? 0 : (Number(this.dataset.delay) || 0) * 1000;
    this.timer = setTimeout(() => {
      // Never steal focus from something the visitor opened themselves — the cart drawer,
      // search, quick view. Nothing is lost by waiting for the next page or session.
      if (document.querySelector(".jg-overlay.is-open, .jg-nav.is-open")) return;
      this.markSeen();
      modal.open();

      // Closing without subscribing is a "not now", not a "never".
      modal.addEventListener("overlay:close", () => {
        if (!this.suppressed()) this.remember("dismissed");
      }, { once: true });
    }, delay);
  }

  disconnectedCallback() {
    if (this.timer) clearTimeout(this.timer);
  }
});
